import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./TermsOfService.module.scss";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";

const TermsOfService = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <div className={styles.container}>
        <div className={styles.content}>
          <Link to="/" className={styles.backButton}>
            <span className="material-icons">arrow_back</span>
            Back to Home
          </Link>

          <h1 className={styles.title}>Terms of Service</h1>

          <section className={styles.section}>
            <h2>Introduction</h2>
            <p>
              These Terms of Service ("Terms") govern your use of Vertik AIC
              Frontend's Gmail integration application (the "Service"). By
              accessing or using our Service, you agree to be bound by these
              Terms.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Acceptance of Terms</h2>
            <p>
              By using our Service, you acknowledge that you have read,
              understood, and agree to be bound by these Terms. If you do not
              agree to these Terms, you may not use the Service.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Description of Service</h2>
            <p>
              Our Service provides Gmail integration functionality that allows
              you to:
            </p>
            <ul>
              <li>Access and manage your Gmail inbox</li>
              <li>Read, compose, and send emails</li>
              <li>Create drafts and send replies</li>
              <li>Organize and search your emails</li>
              <li>View email metadata and statistics</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>User Responsibilities</h2>

            <h3>1. Account Security</h3>
            <ul>
              <li>
                You are responsible for maintaining the security of your Gmail
                account
              </li>
              <li>You must not share your account credentials with others</li>
              <li>
                You must notify us immediately of any unauthorized access to
                your account
              </li>
            </ul>

            <h3>2. Acceptable Use</h3>
            <p>
              You agree to use the Service only for lawful purposes and in
              accordance with these Terms. You agree not to:
            </p>
            <ul>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Transmit any harmful, threatening, or offensive content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use the Service to spam or send unsolicited emails</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>

            <h3>3. Gmail Account Requirements</h3>
            <ul>
              <li>You must have a valid Gmail account to use the Service</li>
              <li>
                You must authorize our application to access your Gmail account
              </li>
              <li>You can revoke access to your Gmail account at any time</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Gmail Integration Terms</h2>

            <h3>1. Google Services</h3>
            <p>
              Our Service integrates with Google's Gmail API and Google Identity
              Services. By using our Service:
            </p>
            <ul>
              <li>You agree to Google's Terms of Service and Privacy Policy</li>
              <li>
                You authorize our application to access your Gmail account as
                needed
              </li>
              <li>
                You understand that Google's terms apply to your Gmail data
              </li>
              <li>You can revoke access through Google's account settings</li>
            </ul>

            <h3>2. Data Access</h3>
            <p>
              Our Service accesses your Gmail data only to provide the
              functionality described in our Privacy Policy. We do not:
            </p>
            <ul>
              <li>Store your email content permanently</li>
              <li>Use your data for advertising purposes</li>
              <li>Share your data with third parties</li>
              <li>Access data beyond what's necessary for the Service</li>
            </ul>

            <h3>3. API Compliance</h3>
            <p>
              We comply with Google's API Services User Data Policy, including:
            </p>
            <ul>
              <li>Limited use of Gmail data</li>
              <li>No selling or sharing of user data</li>
              <li>No advertising use of Gmail data</li>
              <li>User control over data access</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Service Availability</h2>

            <h3>1. Availability</h3>
            <ul>
              <li>
                We strive to maintain high service availability but cannot
                guarantee uninterrupted access
              </li>
              <li>
                The Service may be temporarily unavailable for maintenance or
                updates
              </li>
              <li>
                We will provide reasonable notice for planned maintenance when
                possible
              </li>
            </ul>

            <h3>2. Modifications</h3>
            <ul>
              <li>
                We reserve the right to modify, suspend, or discontinue the
                Service at any time
              </li>
              <li>We may update these Terms from time to time</li>
              <li>
                Continued use of the Service after changes constitutes
                acceptance of new Terms
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Intellectual Property</h2>

            <h3>1. Our Rights</h3>
            <ul>
              <li>
                The Service and its original content, features, and
                functionality are owned by Vertik AIC Frontend
              </li>
              <li>
                Our trademarks, logos, and service marks are protected by
                intellectual property laws
              </li>
            </ul>

            <h3>2. Your Rights</h3>
            <ul>
              <li>You retain ownership of your Gmail data and content</li>
              <li>
                You grant us limited rights to access your Gmail data as
                necessary to provide the Service
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our collection and use of your
              information is governed by our
              <Link to="/privacy-policy" className={styles.inlineLink}>
                {" "}
                Privacy Policy
              </Link>
              , which is incorporated into these Terms by reference.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Limitation of Liability</h2>

            <h3>1. Disclaimer of Warranties</h3>
            <div className={styles.disclaimer}>
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
                NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
            </div>

            <h3>2. Limitation of Damages</h3>
            <div className={styles.disclaimer}>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, VERTIK AIC FRONTEND
                SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO
                LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING TO
                YOUR USE OF THE SERVICE.
              </p>
            </div>

            <h3>3. Maximum Liability</h3>
            <div className={styles.disclaimer}>
              <p>
                OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING OUT OF OR
                RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE
                AMOUNT YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS
                PRECEDING THE CLAIM.
              </p>
            </div>
          </section>

          <section className={styles.section}>
            <h2>Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Vertik AIC Frontend, its
              officers, directors, employees, and agents from any claims,
              damages, or expenses (including reasonable attorneys' fees)
              arising out of or relating to your use of the Service or violation
              of these Terms.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Termination</h2>

            <h3>1. Termination by You</h3>
            <ul>
              <li>You may stop using the Service at any time</li>
              <li>
                You can revoke access to your Gmail account through Google's
                settings
              </li>
              <li>You can delete your account and associated data</li>
            </ul>

            <h3>2. Termination by Us</h3>
            <p>
              We may terminate or suspend your access to the Service
              immediately, without prior notice, for any reason, including if
              you breach these Terms.
            </p>

            <h3>3. Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service will cease
              immediately. We may delete your account and associated data.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of [Your Jurisdiction], without regard to its conflict of
              law provisions.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Dispute Resolution</h2>

            <h3>1. Informal Resolution</h3>
            <p>
              We encourage you to contact us first to resolve any disputes
              informally. You can reach us at legal@vertik.ai.
            </p>

            <h3>2. Binding Arbitration</h3>
            <p>
              Any disputes arising out of or relating to these Terms or the
              Service shall be resolved through binding arbitration in
              accordance with the rules of [Arbitration Organization].
            </p>
          </section>

          <section className={styles.section}>
            <h2>Severability</h2>
            <p>
              If any provision of these Terms is held to be invalid or
              unenforceable, the remaining provisions will remain in full force
              and effect.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the
              entire agreement between you and Vertik AIC Frontend regarding the
              Service.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className={styles.contactInfo}>
              <p>
                <strong>Email:</strong> legal@vertik.ai
              </p>
              <p>
                <strong>Address:</strong> Vertik AIC Frontend Legal Team
              </p>
              <p>
                <strong>Response Time:</strong> We will respond to your inquiry
                within 30 days
              </p>
            </div>
          </section>

          <section className={styles.section}>
            <h2>Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of
              any changes by:
            </p>
            <ul>
              <li>Posting the new Terms on this page</li>
              <li>
                Updating the "Last Updated" date at the top of these Terms
              </li>
              <li>Sending email notifications for significant changes</li>
            </ul>
            <p>
              Your continued use of the Service after any changes constitutes
              acceptance of the updated Terms.
            </p>
          </section>

          <div className={styles.footer}>
            <div className={styles.meta}>
              <p>
                <strong>Effective Date:</strong> January 15, 2025
              </p>
              <p>
                <strong>Last Updated:</strong> January 15, 2025
              </p>
            </div>
            <p>
              These Terms of Service are effective as of January 15, 2025, and
              will remain in effect except with respect to any changes in their
              provisions in the future, which will be in effect immediately
              after being posted on this page.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfService;
